import React from 'react';
import type { RiskDashboardOverview } from '../../contracts/riskSummaryContracts';

export interface RiskOverviewModuleProps {
  enabled: boolean;
  top?: number;
}

const RiskOverviewModule = ({ enabled, top = 5 }: RiskOverviewModuleProps): JSX.Element | null => {
  const [riskOverview, setRiskOverview] = React.useState<RiskDashboardOverview | null>(null);
  const [riskLoading, setRiskLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) {
      setRiskOverview(null);
      return;
    }
    let disposed = false;
    const run = async (): Promise<void> => {
      setRiskLoading(true);
      try {
        const response = await fetch(`/api/admin/risk/overview?top=${encodeURIComponent(String(top))}`, {
          credentials: 'same-origin'
        });
        if (!response.ok) {
          throw new Error(`Risk overview ${response.status}`);
        }
        const json = (await response.json()) as RiskDashboardOverview;
        if (!disposed) {
          setRiskOverview(json);
        }
      } catch {
        if (!disposed) {
          setRiskOverview(null);
        }
      } finally {
        if (!disposed) {
          setRiskLoading(false);
        }
      }
    };
    void run();
    return () => {
      disposed = true;
    };
  }, [enabled, top]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="card">
      <h3>Risk overview</h3>
      <p className="muted">
        Hosted cross-instance summary from active Edinshall Audit installations. This module is designed to stand alone on the portal dashboard.
      </p>
      {riskLoading && <p className="muted">Loading control-plane risk summary…</p>}
      {!riskLoading && riskOverview && (
        <>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span className="badge-subtle">Instances: {riskOverview.totals.reportingInstances}</span>
            <span className="badge-subtle">Audits: {riskOverview.totals.reportingAudits}</span>
            <span className="badge-subtle">Findings with risk: {riskOverview.totals.findingsWithRisk}</span>
            <span className="badge-subtle">High risk: {riskOverview.totals.highRiskFindings}</span>
            <span className="badge-subtle">Accepted: {riskOverview.totals.acceptedFindings}</span>
            <span className="badge-subtle">Accepted high risk: {riskOverview.totals.acceptedHighRiskFindings}</span>
            <span className="badge-subtle">Overdue mitigations: {riskOverview.totals.overdueMitigations}</span>
            <a className="cta secondary" href="/risk">Open risk review</a>
          </div>
          {riskOverview.topFindings.length > 0 && (
            <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
              {riskOverview.topFindings.slice(0, top).map((item) => (
                <div
                  key={`${item.instanceId}-${item.findingId}`}
                  style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}
                >
                  <strong>{item.findingTitle}</strong>
                  <div className="muted">
                    {item.auditTitle}
                    {item.siteUrl ? ` • ${item.siteUrl}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                    <span className="badge-subtle">
                      Current: {item.residualRating || item.initialRating || 'Unrated'}
                    </span>
                    <span className="badge-subtle">
                      Score: {item.residualScore ?? item.initialScore ?? '—'}
                    </span>
                    <span className="badge-subtle">
                      Mitigations: {item.mitigationActionsCompleted}/{item.mitigationActionCount}
                    </span>
                    <span className="badge-subtle">
                      {item.riskAccepted ? 'Accepted' : 'Acceptance pending'}
                    </span>
                  </div>
                  {(item.riskAcceptedBy || item.riskAcceptedAt) && (
                    <div className="muted" style={{ marginTop: '4px' }}>
                      {item.riskAcceptedBy ? `Accepted by ${item.riskAcceptedBy}` : 'Accepted'}
                      {item.riskAcceptedAt ? ` on ${new Date(item.riskAcceptedAt).toLocaleString()}` : ''}
                    </div>
                  )}
                  {item.riskAcceptanceRationale && (
                    <div className="muted" style={{ marginTop: '4px' }}>
                      Rationale: {item.riskAcceptanceRationale}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {!riskLoading && !riskOverview && (
        <p className="muted">No hosted risk summary is available yet for this tenant.</p>
      )}
    </div>
  );
};

export default RiskOverviewModule;
